import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Chip,
} from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { useChartTheme } from '../../themes/chartTheme';
import { determineAggregation, AggregationType } from '../../helpers/chartUtils';
import { formatNumber } from '../../helpers/numberUtils';
import {
  fetchDoiABTest,
  DoiABTestData,
  DoiABTestTimelineRow,
} from '../../helpers/adEvaluationData';
import moment from 'moment';

const AB_TEST_START = '2026-03-04';

const ABTestTab: React.FC = () => {
  const chartTheme = useChartTheme();

  const [startDate, setStartDate] = useState(AB_TEST_START);
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DoiABTestData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const aggregation = useMemo((): AggregationType => {
    return determineAggregation(new Date(startDate), new Date(endDate));
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchDoiABTest(startDate, endDate, aggregation);
        if (!cancelled) setData(result);
      } catch (err) {
        console.error('Error fetching DOI A/B test data:', err);
        if (!cancelled) setError('Fehler beim Laden der A/B-Test-Daten');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, aggregation]);

  const barData = useMemo(() => {
    if (!data?.timeline.length) return [];
    return data.timeline.map((row: DoiABTestTimelineRow) => ({
      period: row.period,
      'V2 Impressions': Number(row.v2_impressions) || 0,
      'V5 Impressions': Number(row.v5_impressions) || 0,
      'V2 VNR Umsatz': Number(row.v2_vnr_revenue) || 0,
      'V5 Direkt-Umsatz': Number(row.v5_plx_revenue) || 0,
    }));
  }, [data]);

  const summary = data?.summary;

  return (
    <Box>
      {/* Info banner */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          A/B Test V2 seit 04.03.2026 — 50/50 Split
        </Typography>
        <Typography variant="body2">
          <strong>Variante 2:</strong> 3 VNR-Reports als Download-Karten |{' '}
          <strong>Variante 5:</strong> Externer Bestell-/Webinar-Ad von
          db.finanztrends.de/api/doi-ad
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Hinweis: Variante 5 enthält auch Fallback-Nutzer, die bereits bei beiden VNR-Kunden
          (GeVestor & Investor) angemeldet sind.
        </Typography>
      </Alert>

      {/* Date inputs */}
      <Box display="flex" gap={2} mb={3}>
        <TextField
          label="Von"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="Bis"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <Typography variant="body2" color="text.secondary" alignSelf="center">
          Aggregation:{' '}
          {aggregation === 'daily'
            ? 'Täglich'
            : aggregation === 'weekly'
              ? 'Wöchentlich'
              : 'Monatlich'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : !summary ? (
        <Alert severity="info">Keine Daten im gewählten Zeitraum.</Alert>
      ) : (
        <>
          {/* Summary cards — side by side */}
          <Grid container spacing={3} mb={3}>
            {/* Variant 2 */}
            <Grid item xs={12} md={6}>
              <Card sx={{ backgroundColor: '#ffffff', height: '100%' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Typography variant="h6" fontWeight="bold">
                      Variante 2
                    </Typography>
                    <Chip label="VNR Reports" size="small" color="primary" />
                  </Box>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <MetricItem
                        label="Impressions"
                        value={formatNumber(summary.variant_2.impressions, 0)}
                        tooltip="Seitenaufrufe mit Variante 2 (VNR-Reports)"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricItem
                        label="Unique Emails"
                        value={formatNumber(summary.variant_2.unique_emails, 0)}
                        tooltip="Eindeutige E-Mail-Adressen, die Variante 2 gesehen haben"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricItem
                        label="VNR Leads"
                        value={formatNumber(summary.variant_2.vnr_leads, 0)}
                        tooltip="Generierte VNR-Leads über die Report-Downloads auf der DOI-Seite. Gezählt aus verlag_affiliates (Netzwerk Finanztrends)."
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricItem
                        label="VNR Umsatz"
                        value={formatNumber(summary.variant_2.vnr_revenue, 2) + ' €'}
                        tooltip="Tatsächliche Vergütung pro Lead (15–17,50 € je nach Report). Realer Umsatz, kein Schätzwert."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <MetricItem
                        label="Umsatz/Impression"
                        value={formatNumber(summary.variant_2.revenue_per_impression, 4) + ' €'}
                        tooltip="VNR Umsatz ÷ Impressions. Hinweis: VNR-Umsatz materialisiert sich verzögert über Wochen/Monate — frühe Werte unterschätzen die tatsächliche Performance."
                        highlight
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Variant 5 */}
            <Grid item xs={12} md={6}>
              <Card sx={{ backgroundColor: '#ffffff', height: '100%' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Typography variant="h6" fontWeight="bold">
                      Variante 5
                    </Typography>
                    <Chip label="API Ad" size="small" color="secondary" />
                  </Box>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <MetricItem
                        label="Impressions"
                        value={formatNumber(summary.variant_5.impressions, 0)}
                        tooltip="Seitenaufrufe mit Variante 5. Enthält auch Fallback-Nutzer (bereits bei beiden VNR-Kunden angemeldet), daher ~19% mehr als V2."
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricItem
                        label="Unique Emails"
                        value={formatNumber(summary.variant_5.unique_emails, 0)}
                        tooltip="Eindeutige E-Mail-Adressen, die Variante 5 gesehen haben"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricItem
                        label="Webinar Regs"
                        value={formatNumber(summary.variant_5.webinar_regs, 0)}
                        tooltip="Webinar-Registrierungen über den DOI-Ad. Hinweis: Banner enthält keine Varianten-ID — Zuordnung erfolgt über Zeitraum ab Teststart."
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricItem
                        label="Webinar Teilnehmer"
                        value={formatNumber(summary.variant_5.webinar_attendees, 0)}
                        tooltip="Tatsächliche Webinar-Teilnehmer (attendance-Event vorhanden)"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricItem
                        label="Webinar-Bestellungen"
                        value={formatNumber(summary.variant_5.webinar_orders, 0)}
                        tooltip="Bestellungen, die zu DOI-Webinar-Registrierungen gehören (Zuordnung Bestellung → Anmeldung über webinar_order_mapping, nicht abgelehnt)."
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricItem
                        label="Webinar-CPA-Wert"
                        value={formatNumber(summary.variant_5.webinar_revenue, 2) + ' €'}
                        tooltip="CPA-Wert der Bestellungen, die zu DOI-Webinar-Registrierungen gehören (Zuordnung über webinar_order_mapping). Bis 16.09.2026 Schätzwert Registrierungen × Ø CPA."
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricItem
                        label="Direkt-Bestellungen"
                        value={formatNumber(summary.variant_5.plx_orders, 0)}
                        tooltip="Abo-Bestellungen über den DOI-Ad (Banner enthält _DOI_SAM_). Nur nicht-abgelehnte Bestellungen (vertrag_sts ≠ V)."
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricItem
                        label="Direkt-CPA-Wert"
                        value={formatNumber(summary.variant_5.plx_revenue, 2) + ' €'}
                        tooltip="CPA-Wert der Direkt-Bestellungen: jede Bestellung mit ihrem eigenen CPA-Wert bewertet (aus ihrer Zahlung, sonst ihrem Angebot). Steuerungsgröße, kein Zahlungseingang."
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricItem
                        label="Gesamt-Umsatz"
                        value={formatNumber(summary.variant_5.total_revenue, 2) + ' €'}
                        tooltip="Direkt-Umsatz (CPA-Bewertung) + Webinar-Umsatz (CPA-Wert der zugeordneten Bestellungen). Beide Werte sind CPA-Bewertungen, kein Zahlungseingang — V2 VNR-Umsatz ist dagegen realer Zahlungseingang."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <MetricItem
                        label="Umsatz/Impression"
                        value={formatNumber(summary.variant_5.revenue_per_impression, 4) + ' €'}
                        tooltip="Gesamt-Umsatz ÷ Impressions. Beachte: V5 enthält Fallback-Nutzer, die den Wert pro Impression nach unten verzerren."
                        highlight
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Avg CPA info */}
          <Alert severity="warning" sx={{ mb: 3 }} icon={false}>
            <Typography variant="body2">
              <strong>Ø CPA pro Webinar-Registrierung:</strong>{' '}
              {formatNumber(summary.avg_cpa_per_registration, 2)} € (90-Tage-Durchschnitt,
              nicht-evergreen Webinare) — nur Referenz, der Webinar-Umsatz oben ist der CPA-Wert der
              tatsächlich zugeordneten Bestellungen.
            </Typography>
          </Alert>

          {/* Bar chart — impressions per variant */}
          {barData.length > 0 && (
            <>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                Impressions & Umsatz pro Zeitraum
              </Typography>
              <Box height={350} mb={3}>
                <ResponsiveBar
                  data={barData}
                  keys={['V2 Impressions', 'V5 Impressions']}
                  indexBy="period"
                  theme={chartTheme}
                  margin={{ top: 20, right: 150, bottom: 50, left: 60 }}
                  padding={0.3}
                  groupMode="grouped"
                  colors={['#2196f3', '#ff9800']}
                  axisBottom={{
                    tickRotation: -45,
                  }}
                  axisLeft={{
                    legend: 'Impressions',
                    legendOffset: -50,
                    legendPosition: 'middle',
                  }}
                  legends={[
                    {
                      dataFrom: 'keys',
                      anchor: 'bottom-right',
                      direction: 'column',
                      translateX: 140,
                      itemWidth: 130,
                      itemHeight: 20,
                      symbolSize: 12,
                    },
                  ]}
                  tooltip={({ id, value, indexValue }) => (
                    <Box
                      sx={{
                        backgroundColor: '#fff',
                        p: 1,
                        border: '1px solid #ccc',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="caption" fontWeight="bold">
                        {String(id)}
                      </Typography>
                      <br />
                      <Typography variant="caption">
                        {String(indexValue)}: {formatNumber(Number(value), 0)}
                      </Typography>
                    </Box>
                  )}
                />
              </Box>
            </>
          )}

          {/* Detail table */}
          <Typography variant="subtitle2" color="text.secondary" mb={1}>
            Detailtabelle
          </Typography>
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <HeaderCell>Zeitraum</HeaderCell>
                  <HeaderCell align="right" tooltip="Variante 2 Seitenaufrufe">
                    V2 Impr.
                  </HeaderCell>
                  <HeaderCell align="right" tooltip="Variante 5 Seitenaufrufe">
                    V5 Impr.
                  </HeaderCell>
                  <HeaderCell align="right" tooltip="VNR-Leads aus Report-Downloads (Variante 2)">
                    V2 Leads
                  </HeaderCell>
                  <HeaderCell align="right" tooltip="Reale VNR-Vergütung pro Lead (Variante 2)">
                    V2 Umsatz
                  </HeaderCell>
                  <HeaderCell
                    align="right"
                    tooltip="Webinar-Registrierungen über DOI-Ad (Variante 5, keine direkte Bestell-Zuordnung möglich)"
                  >
                    V5 Regs
                  </HeaderCell>
                  <HeaderCell
                    align="right"
                    tooltip="Direkt-Bestellungen über DOI-Ad (Variante 5, CPA-Bewertung)"
                  >
                    V5 Best.
                  </HeaderCell>
                  <HeaderCell
                    align="right"
                    tooltip="Direkt-CPA-Wert über DOI-Ad (CPA-Wert je Bestellung, Variante 5)"
                  >
                    V5 Umsatz
                  </HeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.timeline.map((row: DoiABTestTimelineRow) => (
                  <TableRow key={row.period}>
                    <TableCell>{row.period}</TableCell>
                    <TableCell align="right">
                      {formatNumber(Number(row.v2_impressions), 0)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(Number(row.v5_impressions), 0)}
                    </TableCell>
                    <TableCell align="right">{formatNumber(Number(row.v2_vnr_leads), 0)}</TableCell>
                    <TableCell align="right">
                      {formatNumber(Number(row.v2_vnr_revenue), 2)} €
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(Number(row.v5_webinar_regs), 0)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(Number(row.v5_plx_orders), 0)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(Number(row.v5_plx_revenue), 2)} €
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

function HeaderCell({
  children,
  align,
  tooltip,
}: {
  children: React.ReactNode;
  align?: 'right' | 'left';
  tooltip?: string;
}) {
  const cell = (
    <TableCell
      sx={{
        backgroundColor: '#e0e0e0 !important',
        fontWeight: 'bold',
        cursor: tooltip ? 'help' : undefined,
      }}
      align={align}
    >
      {children}
    </TableCell>
  );
  if (!tooltip) return cell;
  return (
    <Tooltip title={tooltip} arrow placement="top">
      {cell}
    </Tooltip>
  );
}

function MetricItem({
  label,
  value,
  tooltip,
  highlight,
}: {
  label: string;
  value: string;
  tooltip?: string;
  highlight?: boolean;
}) {
  const content = (
    <Box
      sx={{
        p: 1,
        borderRadius: 1,
        backgroundColor: highlight ? '#e3f2fd' : 'transparent',
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ cursor: tooltip ? 'help' : undefined }}
      >
        {label}
      </Typography>
      <Typography variant={highlight ? 'h6' : 'body1'} fontWeight="bold">
        {value}
      </Typography>
    </Box>
  );
  if (!tooltip) return content;
  return (
    <Tooltip title={tooltip} arrow placement="top">
      {content}
    </Tooltip>
  );
}

export default ABTestTab;
